import { ApiProduct, ApiProductCategoryTag } from 'idt-ecommerce-api/_api/api-catalog/api-products/api-products.models';
import { EcommerceContext } from 'idt-ecommerce-api/context/context.models';
import { UrlsWebHelpers } from 'idt-ecommerce-api/util/urls-web.helpers';

import { ProductMetadata, ProductFilterData, ProductLabel, ProductFiltersMeta, ProductLabelPosition } from './product-metadata.models';


export class ProductMetadataModelsTransformer {

  public static parseProductMetadata(apiProduct: ApiProduct, fromDetail: boolean, context: EcommerceContext): ProductMetadata {
    const mocacoOrEstilismoSourceProductId = apiProduct.mocacoOrEstilismoSourceProductId as string;
    const filtersData = this.parseProductMetadataFiltersData(apiProduct, fromDetail, context);
    const labels = this.parseProductMetadataLabels(apiProduct, fromDetail, context);
    const stylePath = this.parseProductMetadataStylePath(apiProduct, fromDetail, context);
    const livesortingOrder = apiProduct.sequence;
    const metadata: ProductMetadata = {
      livesortingOrder,
      fromDetail,
      filtersData,
      labels,
      stylePath,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      _debug: { _mocacoOrEstilismoSourceProductId: mocacoOrEstilismoSourceProductId }
    };

    return metadata;
  }

  // ---- Private functions ----

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private static parseProductMetadataFiltersData(apiProduct: ApiProduct, fromDetail: boolean, context: EcommerceContext): ProductFilterData[] {
    const filtersData: ProductFilterData[] = [];

    const colors: ProductFiltersMeta[] = [];
    const features: ProductFiltersMeta[] = [];
    const sizes: ProductFiltersMeta[] = [];
    const qualities: ProductFiltersMeta[] = [];
    const rapids: ProductFiltersMeta[] = [];

    if (apiProduct.attributes) {
      for (const attribute of apiProduct.attributes) {
        if (!attribute.value || (attribute.name && attribute.name.includes('HIDDEN'))) {
          continue;
        }

        switch (attribute.type) { // eslint-disable-line default-case
          case 'XCOLFILTER':
            colors.push({ value: attribute.value, name: attribute.name, type: attribute.type });
            break;
          case 'XCATFILTER':
            features.push({ value: attribute.value, name: attribute.name, type: attribute.type });
            break;
          case 'XCALFILTER':
            qualities.push({ value: attribute.value, name: attribute.name, type: attribute.type });
            rapids.push({ value: attribute.value, name: attribute.name, type: attribute.type });
            break;
          case 'XBRAFILTER':
            rapids.push({ value: attribute.value, name: attribute.name, type: attribute.type });
            break;
        }
      }
    }

    if (apiProduct.detail.colors) {
      for (const color of apiProduct.detail.colors) {
        if (color.sizes) {
          for (const size of color.sizes) {
            sizes.push({ value: size.name, name: '' });
          }
        }
      }
    }

    if (colors.length > 0) {
      filtersData.push({ type: 'ColorFilter', values: colors });
    }

    if (features.length > 0) {
      filtersData.push({ type: 'FeatureFilter', values: features });
    }

    if (sizes.length > 0) {
      filtersData.push({ type: 'SizeFilter', values: sizes });
    }

    if (qualities.length > 0) {
      filtersData.push({ type: 'QualityFilter', values: qualities });
    }

    if (rapids.length > 0) {
      filtersData.push({ type: 'RapidFilter', values: rapids });
    }

    return filtersData;
  }

  // supported: Triman, MadeIn, New, JoinLife
  private static parseProductMetadataLabels(apiProduct: ApiProduct, fromDetail: boolean, context: EcommerceContext): ProductLabel[] {
    const labels: ProductLabel[] = [];

    if (isDefined(apiProduct.attributes)) {
      // triman (only applies in France)
      const triman = apiProduct.attributes.find((attribute) => {
        return context.store.countryCode === 'FR' && attribute.name === 'TRIMAN';
      });
      if (triman && triman.value) {
        labels.push({ type: 'Triman', value: triman.value });
      }

      // madeIn
      const madeIn = apiProduct.attributes.find((attribute) => {
        return isDefined(attribute.name) && attribute.name.startsWith('MADEIN_');
      });
      if (madeIn && madeIn.value) {
        labels.push({ type: 'MadeIn', value: madeIn.value });
      }
    }

    if (isDefined(apiProduct.tags)) {
      for (const tag of apiProduct.tags) {
        if (tag.attachments.length === 0) {
          continue;
        }

        for (const attachment of tag.attachments) {
          if (attachment.type !== 'LABEL') {
            continue;
          }

          let label: ProductLabel = {
            type: 'Generic',
            value: attachment.name,
            url: UrlsWebHelpers.buildStaticUrl(context.store, attachment.path),
            categories: tag.category.identifier?.split('_'),
            isShowed: tag.category.identifier?.includes('_LABEL'),
            position: this.parseProductMetadataTagPosition(tag.category)
          };

          if (attachment.name === 'Join Life') {
            const description = apiProduct.detail.joinLife ? apiProduct.detail.joinLife : undefined;
            label = { ...label, type: 'JoinLife', description };
          }
          labels.push(label);
        }
      }
    }

    return labels;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private static parseProductMetadataStylePath(apiProduct: ApiProduct, fromDetail: boolean, context: EcommerceContext) {
    return apiProduct.detail.colors?.[0]?.image?.style?.[0];
  }

  private static parseProductMetadataTagPosition(category: ApiProductCategoryTag) {
    const identifier = category.identifier;
    let position: ProductLabelPosition = 'bottom';

    if (identifier?.includes('_TOP')) {
      position = 'top';
    } else if (identifier?.includes('_OVER')) {
      if (identifier?.includes('_TR')) {
        position = 'top_right';
      } else if (identifier?.includes('_TL')) {
        position = 'top_left';
      } else if (identifier?.includes('_BL')) {
        position = 'bottom_left';
      } else if (identifier?.includes('_BR')) {
        position = 'bottom_right';
      }
    }
    return position;
  }

}
